import { useLazyQuery, useQuery } from '@apollo/client';
import type {
  HistoryFetchProps,
  HistoryFetchResponse,
} from '@openx/components-core/utils/context/CoreContextTypes.tsx';
import type { AuditTrail } from '@openx/types/auditTrail.ts';
import type { Resource } from '@openx/types/resources.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetAuditTrailDocument } from '../../../graphql/query/auditTrail/GetAuditTrail.generated.tsx';

export const useHistoryFetch = <R extends Resource>({
  id,
  resource,
  isLoading,
}: HistoryFetchProps<R>): HistoryFetchResponse => {
  const {
    data: { auditTrail } = {},
    loading: queryLoading,
    fetchMore,
  } = useQuery(GetAuditTrailDocument, {
    skip: !id,
    variables: {
      obj_id: id ?? '',
      obj_type: resource,
      limit: 50,
      offset: 0,
    },
  });

  const [fetchAuditTrail] = useLazyQuery(GetAuditTrailDocument);

  const [createAuditTrail, setCreateAuditTrail] = useState<AuditTrail | undefined>();
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);

  const hasMore = auditTrail?.has_more || false;
  const trails = useMemo(() => auditTrail?.objects ?? [], [auditTrail?.objects]) as AuditTrail[];

  const shouldGetCreateAuditTrail = hasMore && !createAuditTrail;
  const loading = isLoading || queryLoading || auditTrailLoading;

  const loadMore = useCallback(async () => {
    setAuditTrailLoading(true);
    await fetchMore({
      variables: {
        offset: trails.length,
      },
    });
    setAuditTrailLoading(false);
  }, [fetchMore, trails.length]);

  const retrieveCreateAuditTrail = useCallback(async () => {
    const { data: { auditTrail } = {} } = await fetchAuditTrail({
      variables: {
        obj_id: id,
        obj_type: resource,
        limit: 1,
        offset: 0,
        revision: 1,
      },
    });

    setCreateAuditTrail(auditTrail?.objects[0] as AuditTrail);
    setAuditTrailLoading(false);
  }, [fetchAuditTrail, id, resource]);

  useEffect(() => {
    if (shouldGetCreateAuditTrail) {
      setAuditTrailLoading(true);
      retrieveCreateAuditTrail();
    }
  }, [retrieveCreateAuditTrail, shouldGetCreateAuditTrail]);

  return { hasMore, loadMore, loading, trails: createAuditTrail && hasMore ? [...trails, createAuditTrail] : trails };
};
